import { useQuery } from '@tanstack/react-query'
import { Brain } from 'lucide-react'
import { mlApi } from '../api/client'
import './MLEnsembleStatus.css'

// Render signed numbers with Meridian's real U+2212 MINUS (not ASCII hyphen).
const signed = (n: number, decimals = 2): string => {
  if (n === 0) return n.toFixed(decimals)
  const sign = n > 0 ? '+' : '−'
  return `${sign}${Math.abs(n).toFixed(decimals)}`
}

interface ModelCardProps {
  name: string
  weight: number
  accuracy?: number
  children: React.ReactNode
}

function ModelCard({ name, weight, accuracy, children }: ModelCardProps) {
  return (
    <div className="model-card">
      <div className="model-header">
        <h4 className="model-name">{name}</h4>
        <div className="model-weight">
          <span className="weight-label">Weight:</span>
          <span className="weight-value">{(weight * 100).toFixed(0)}%</span>
        </div>
      </div>
      {accuracy !== undefined && (
        <div className="model-accuracy">7-day accuracy: <span className="mono">{accuracy.toFixed(1)}%</span></div>
      )}
      <div className="model-content">{children}</div>
    </div>
  )
}

export function MLEnsembleStatus() {
  const { data: ensemble, isLoading, isError } = useQuery({
    queryKey: ['ml-ensemble'],
    queryFn: mlApi.getEnsemble,
    refetchInterval: 30000,
  })

  const recommendationColors: Record<string, string> = {
    strong_buy: 'strong-buy',
    buy: 'buy',
    hold: 'hold',
    sell: 'sell',
    strong_sell: 'strong-sell',
  }

  const recommendation = ensemble?.blended.recommendation ?? 'hold'
  const recommendationClass = recommendationColors[recommendation] ?? 'hold'

  return (
    <div className="card ml-ensemble-card">
      <div className="card-header">
        <h3 className="card-title">
          <Brain size={20} className="icon" />
          Signal Providers
        </h3>
        {ensemble && !ensemble.pipeline_enabled && (
          <span className="recommendation-badge hold">PIPELINE OFF</span>
        )}
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : isError || !ensemble ? (
        <div className="ml-not-configured">
          <Brain size={20} className="icon" />
          <span>Unable to load signal status</span>
        </div>
      ) : (
        <>
          {/* Blended summary */}
          <div className="meta-learner-summary">
            <div className="summary-left">
              <div className="summary-label">Combined Signal</div>
              <div
                className={`summary-score ${ensemble.blended.combined_score > 0 ? 'up' : ensemble.blended.combined_score < 0 ? 'down' : ''}`}
              >
                {signed(ensemble.blended.combined_score)}
              </div>
            </div>
            <div className="summary-right">
              <div className="summary-label">Recommendation</div>
              <span className={`recommendation-badge ${recommendationClass}`}>
                {recommendation.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

          <div className="agreement-bar">
            <span className="agreement-label">Model Agreement:</span>
            <div className="agreement-track">
              <div
                className="agreement-fill"
                style={{ width: `${ensemble.blended.agreement_level * 100}%` }}
              />
            </div>
            <span className="agreement-value">
              {(ensemble.blended.agreement_level * 100).toFixed(0)}%
            </span>
          </div>

          {/* Dynamic provider cards */}
          <div className="models-list">
            {ensemble.providers.map((p) => (
              <ModelCard key={p.name} name={p.name} weight={p.weight}>
                <div className="model-stats">
                  <div className="stat-row">
                    <span className="stat-label">Signal:</span>
                    <span className="stat-value mono">{p.signal.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Confidence:</span>
                    <span className="stat-value mono">{(p.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Type:</span>
                    <span className="stat-value mono">{p.cross_sectional ? 'cross-sectional' : 'per-symbol'}</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">{p.detail}</span>
                  </div>
                </div>
              </ModelCard>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
